import { AdminService, ExperimentalUser } from '@/utils/apis/admin-service';
import React, { useEffect, useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';

interface AdminUserManagerProps {
  onUserAdded?: () => void;
}

export const AdminUserManager: React.FC<AdminUserManagerProps> = ({ onUserAdded }) => {
  const [users, setUsers] = useState<ExperimentalUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingUser, setEditingUser] = useState<ExperimentalUser | null>(null);
  
  // Form states
  const [email, setEmail] = useState('');
  const [notes, setNotes] = useState('');

  // Fetch current admin users
  useEffect(() => {
    fetchAdminUsers();
  }, []);

  const fetchAdminUsers = async () => {
    try {
      console.log('👑 Fetching admin users using feature-type endpoint...');
      const fetchedUsers = await AdminService.getExperimentalUsersByFeatureType('admin');
      console.log('👑 Admin users fetched:', fetchedUsers);
      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Error fetching admin users:', error);
    }
  };

  const handleAddAdmin = async () => {
    if (!email.trim()) {
      setMessage('❌ Please enter an email address');
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await AdminService.addExperimentalUser({
        email: email.trim(),
        featureType: 'admin',
        environment: 'dev', // Keep for now
        notes: notes.trim() || undefined
      });

      if (response.success) {
        setMessage(`✅ ${response.message}`);
        setEmail('');
        setNotes('');
        setShowAddForm(false);
        fetchAdminUsers(); // Refresh the list
        onUserAdded?.();
      } else {
        setMessage(`❌ ${response.message}`);
      }
    } catch (error) {
      setMessage('❌ Failed to add admin user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditAdmin = (user: ExperimentalUser) => {
    setEditingUser(user);
    setEmail(user.email);
    setNotes(user.notes || '');
    setShowAddForm(true);
  };

  const handleUpdateAdmin = async () => {
    if (!editingUser || !email.trim()) {
      setMessage('❌ Please enter an email address');
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      // For now, we'll remove and re-add the user since we don't have an update endpoint
      const removeResponse = await AdminService.removeExperimentalUser(editingUser.id);
      if (removeResponse.success) {
        const addResponse = await AdminService.addExperimentalUser({
          email: email.trim(),
          featureType: 'admin',
          environment: 'dev', // Keep for now
          notes: notes.trim() || undefined
        });

        if (addResponse.success) {
          setMessage(`✅ Admin user updated successfully`);
          setEmail('');
          setNotes('');
          setShowAddForm(false);
          setEditingUser(null);
          fetchAdminUsers();
          onUserAdded?.();
        } else {
          setMessage(`❌ ${addResponse.message}`);
        }
      } else {
        setMessage(`❌ ${removeResponse.message}`);
      }
    } catch (error) {
      setMessage('❌ Failed to update admin user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveAdmin = async (userId: string) => {
    if (!window.confirm('Are you sure you want to remove this admin user? This will revoke their admin access.')) {
      return;
    }

    try {
      const response = await AdminService.removeExperimentalUser(userId);
      if (response.success) {
        setMessage(`✅ ${response.message}`);
        fetchAdminUsers(); // Refresh the list
      } else {
        setMessage(`❌ ${response.message}`);
      }
    } catch (error) {
      setMessage('❌ Failed to remove admin user. Please try again.');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      editingUser ? handleUpdateAdmin() : handleAddAdmin();
    }
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingUser(null);
    setEmail('');
    setNotes('');
    setMessage(null);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header with Add Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
            Admin Users ({users.length})
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Users with administrative access to the platform
          </p>
        </div>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          variant={showAddForm ? "outline" : "default"}
          size="sm"
          className="w-full sm:w-auto"
        >
          {showAddForm ? '✕ Cancel' : '➕ Add Admin'}
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="bg-white/50 backdrop-blur-sm border-0 shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-lg">
              {editingUser ? '✏️ Edit' : '➕ Add'} Admin User
            </CardTitle>
            <CardDescription>
              {editingUser ? 'Update admin user details' : 'Grant administrative access to a new user'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="admin-email">Email Address</Label>
                <Input
                  id="admin-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="admin@example.com"
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-notes">Notes (Optional)</Label>
                <Input
                  id="admin-notes"
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Why this user was granted admin access"
                  disabled={loading}
                />
              </div>
            </div>

            {message && (
              <div className={`p-3 rounded-lg text-sm border ${
                message.startsWith('✅') 
                  ? 'bg-green-50 text-green-800 border-green-200' 
                  : 'bg-red-50 text-red-800 border-red-200'
              }`}>
                {message}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
              <Button
                onClick={cancelForm}
                variant="outline"
                disabled={loading}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                onClick={editingUser ? handleUpdateAdmin : handleAddAdmin}
                disabled={!email.trim() || loading}
                className="w-full sm:w-auto"
              >
                {loading ? '⏳ Processing...' : (editingUser ? '💾 Update Admin' : '➕ Add Admin')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Users List */}
      {users.length > 0 ? (
        <div className="space-y-3">
          {users.map((user) => (
            <Card key={user.id} className="bg-white/80 backdrop-blur-sm border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-start space-x-3">
                    <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 text-sm">👑</span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.email}
                      </p>
                      {user.notes && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {user.notes}
                        </p>
                      )}
                      <div className="flex items-center space-x-2 mt-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Admin
                        </span>
                        <span className="text-xs text-gray-500">
                          Added by {user.addedBy}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <Button
                      onClick={() => handleEditAdmin(user)}
                      variant="outline"
                      size="sm"
                      className="text-xs"
                    >
                      ✏️ Edit
                    </Button>
                    <Button
                      onClick={() => handleRemoveAdmin(user.id)}
                      variant="outline"
                      size="sm"
                      className="text-xs text-red-600 border-red-200 hover:bg-red-50"
                    >
                      🗑️ Remove
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="bg-white/50 backdrop-blur-sm border-0 shadow-sm">
          <CardContent className="p-8 text-center">
            <div className="text-gray-400 text-4xl mb-4">
              👑
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No Admin Users
            </h3>
            <p className="text-gray-600 text-sm">
              No admin users are currently configured. Add the first admin user to get started.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}; 